import {createApi, fetchBaseQuery} from "@reduxjs/toolkit/query/react";

import {IMonthCityStats} from "../../../../../api/src/db_queries/month_city_stats_query";
import {IMonthCityTypeStats} from "../../../../../api/src/db_queries/month_city_type_stats_query";
import {IMonthDistrictStats} from "../../../../../api/src/db_queries/month_district_stats_query";
import {IMonthDistrictTypeStats} from "../../../../../api/src/db_queries/month_district_type_stats_query";
import {IMonthGusStats} from "../../../../../api/src/db_queries/month_gus_stats_query";
import {IQuarterCityStats} from "../../../../../api/src/db_queries/quarter_city_stats_query";
import {IQuarterCityTypeStats} from "../../../../../api/src/db_queries/quarter_city_type_stats_query";
import prepare_headers from "../../../store/prepare_headers";

export type DistrictStats = IMonthDistrictStats | IMonthDistrictTypeStats;

const normalizeChartsListResponse = <T>(response: unknown): T[] => {
    if (Array.isArray(response)) {
        return response;
    }

    if (response != null && typeof response === "object") {
        const record = response as {results?: unknown; data?: unknown};

        if (Array.isArray(record.results)) {
            return record.results as T[];
        }

        if (Array.isArray(record.data)) {
            return record.data as T[];
        }
    }

    return [];
};

export interface ChartsPlatformApiParams {
    region_id: number;
    date_start: string;
    date_end: string;
    scenario: string;
    offer_type?: string;
}

export interface ChartsPlatformHomeParams {
    date_start: string;
    date_end: string;
    scenario: string;
}

export const chartsPlatformApi = createApi({
    reducerPath: "chartsPlatformApi",
    baseQuery: fetchBaseQuery({
        baseUrl: `/api/charts/`,
        prepareHeaders: prepare_headers,
        credentials: "include"
    }),
    endpoints: (builder) => ({
        getMonthCityStats: builder.query<IMonthCityStats[], ChartsPlatformApiParams>({
            query: (params) => ({
                url: "month-stats/",
                method: "GET",
                params
            }),
            transformResponse: normalizeChartsListResponse<IMonthCityStats>
        }),
        getMonthCityTypeStats: builder.query<IMonthCityTypeStats[], ChartsPlatformApiParams>({
            query: (params) => ({
                url: "month-type-stats/",
                method: "GET",
                params
            }),
            transformResponse: normalizeChartsListResponse<IMonthCityTypeStats>
        }),
        getMonthDistrictStats: builder.query<DistrictStats[], ChartsPlatformApiParams>({
            query: (params) => ({
                url: params.offer_type ? "month-district-type-stats/" : "month-district-stats/",
                method: "GET",
                params
            }),
            transformResponse: normalizeChartsListResponse<DistrictStats>
        }),
        getMonthGusStatsPermits: builder.query<IMonthGusStats[], ChartsPlatformApiParams>({
            query: (params) => ({
                url: "month-gus-stats/permits/",
                method: "GET",
                params
            }),
            transformResponse: normalizeChartsListResponse<IMonthGusStats>
        }),
        getMonthGusStatsStarted: builder.query<IMonthGusStats[], ChartsPlatformApiParams>({
            query: (params) => ({
                url: "month-gus-stats/started/",
                method: "GET",
                params
            }),
            transformResponse: normalizeChartsListResponse<IMonthGusStats>
        }),
        getMonthGusStatsFinished: builder.query<IMonthGusStats[], ChartsPlatformApiParams>({
            query: (params) => ({
                url: "month-gus-stats/finished/",
                method: "GET",
                params
            }),
            transformResponse: normalizeChartsListResponse<IMonthGusStats>
        }),
        getQuarterCityStats: builder.query<IQuarterCityStats[], ChartsPlatformApiParams>({
            query: (params) => ({
                url: "quarter-stats/",
                method: "GET",
                params
            }),
            transformResponse: normalizeChartsListResponse<IQuarterCityStats>
        }),
        getQuarterCityTypeStats: builder.query<IQuarterCityTypeStats[], ChartsPlatformApiParams>({
            query: (params) => ({
                url: "quarter-type-stats/",
                method: "GET",
                params
            }),
            transformResponse: normalizeChartsListResponse<IQuarterCityTypeStats>
        }),
        getBiggestRegionsStats: builder.query<IMonthCityStats[], ChartsPlatformHomeParams>({
            query: (params) => ({
                url: "biggest-regions-stats/",
                method: "GET",
                params
            }),
            transformResponse: normalizeChartsListResponse<IMonthCityStats>
        }),
        getBiggestRegionsTypeStats: builder.query<IMonthCityTypeStats[], ChartsPlatformHomeParams>({
            query: (params) => ({
                url: "biggest-regions-type-stats/",
                method: "GET",
                params
            }),
            transformResponse: normalizeChartsListResponse<IMonthCityTypeStats>
        })
    })
});

export const {
    useGetMonthCityStatsQuery: useGetChartsMonthCityStatsQuery,
    useGetMonthCityTypeStatsQuery: useGetChartsMonthCityTypeStatsQuery,
    useGetMonthDistrictStatsQuery: useGetChartsMonthDistrictStatsQuery,
    useGetMonthGusStatsPermitsQuery: useGetChartsMonthGusStatsPermitsQuery,
    useGetMonthGusStatsStartedQuery: useGetChartsMonthGusStatsStartedQuery,
    useGetMonthGusStatsFinishedQuery: useGetChartsMonthGusStatsFinishedQuery,
    useGetQuarterCityStatsQuery: useGetChartsQuarterCityStatsQuery,
    useGetQuarterCityTypeStatsQuery: useGetChartsQuarterCityTypeStatsQuery,
    useGetBiggestRegionsStatsQuery: useGetChartsBiggestRegionsStatsQuery,
    useGetBiggestRegionsTypeStatsQuery: useGetChartsBiggestRegionsTypeStatsQuery
} = chartsPlatformApi;
